import { Currency, Price, Token } from '@uniswap/sdk-core'
import { useEffect, useMemo, useState } from 'react'
import { config } from 'uniswap/src/config'
import { PollingInterval } from 'uniswap/src/constants/misc'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { logger } from 'utilities/src/logger/logger'

// Mapping from UniverseChainId to Alchemy Prices API network slug
const ALCHEMY_NETWORK_BY_CHAIN: Partial<Record<UniverseChainId, string>> = {
  [UniverseChainId.RobinhoodChain]: 'robinhood-mainnet',
}

// Chains that sdk-core does NOT support — these use Alchemy Prices API instead of trading API
const NON_SDK_CHAIN_IDS: UniverseChainId[] = []

export function isNonSdkChain(chainId: number | undefined): boolean {
  if (!chainId) {
    return false
  }
  return NON_SDK_CHAIN_IDS.some((c) => c === chainId)
}

interface AlchemyPriceResult {
  price: Price<Currency, Currency> | undefined
  isLoading: boolean
}

/**
 * Fetches USD price of a token via Alchemy Prices API.
 * Used for chains not supported by sdk-core.
 *
 * Returns a Price<Currency, Token> where the quote token is the chain's
 * stablecoin, so price.quote(amount) yields a stablecoin-denominated
 * CurrencyAmount consistent with the classic useUSDCPrice path.
 */
export function useAlchemyTokenPrice(
  currency: Currency | undefined,
  quoteCurrency: Token | undefined,
  pollInterval: PollingInterval = PollingInterval.Fast,
): AlchemyPriceResult {
  const [usdPrice, setUsdPrice] = useState<number | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const chainId = currency?.chainId
  const network = chainId ? ALCHEMY_NETWORK_BY_CHAIN[chainId as UniverseChainId] : undefined
  const tokenAddress = currency?.isNative ? undefined : currency?.address
  const symbol = currency?.isNative ? currency.symbol : undefined

  useEffect(() => {
    if (!currency || !network || (!tokenAddress && !symbol)) {
      setUsdPrice(undefined)
      return undefined
    }

    let cancelled = false
    const apiKey = config.alchemyApiKey

    const fetchPrice = async (): Promise<void> => {
      setIsLoading(true)
      try {
        let url: string
        let body: BodyInit | undefined

        if (symbol) {
          url = `https://api.g.alchemy.com/prices/v1/${apiKey}/tokens/by-symbol?symbols=${encodeURIComponent(symbol)}`
          body = undefined
        } else {
          url = `https://api.g.alchemy.com/prices/v1/${apiKey}/tokens/by-address`
          body = JSON.stringify({
            addresses: [{ network, address: tokenAddress }],
          })
        }

        const response = await fetch(url, {
          method: symbol ? 'GET' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        })

        if (!response.ok) {
          throw new Error(`Alchemy Prices API returned ${response.status}`)
        }

        const data = await response.json()
        const priceEntry = data.data?.[0]
        const usdValue = priceEntry?.prices?.find(
          (p: { currency: string }) => p.currency.toLowerCase() === 'usd',
        )?.value

        if (!cancelled) {
          setUsdPrice(usdValue ? parseFloat(usdValue) : undefined)
        }
      } catch (error) {
        if (!cancelled) {
          logger.error(error as Error, {
            tags: { file: 'useAlchemyTokenPrice', function: 'fetchPrice' },
            extra: { chainId, network, tokenAddress, symbol },
          })
          setUsdPrice(undefined)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchPrice().catch(() => {})

    const intervalMs = pollInterval === PollingInterval.Fast ? 10_000 : 30_000
    const interval = setInterval(() => fetchPrice().catch(() => {}), intervalMs)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [currency, network, tokenAddress, symbol, pollInterval, chainId])

  return useMemo(() => {
    if (!currency || usdPrice === undefined) {
      return { price: undefined, isLoading }
    }

    if (!quoteCurrency) {
      return { price: undefined, isLoading }
    }

    // Price(base=currency, quote=quoteCurrency, denominator, numerator)
    // means 1 raw unit of base = numerator/denominator raw units of quote
    //
    // usdPrice is USD per 1 whole token (human-readable).
    // Since the stablecoin is ~1 USD, we want:
    //   1 raw base = usdPrice * 10^(quoteDecimals - baseDecimals) raw quote
    const scale = Math.pow(10, quoteCurrency.decimals - currency.decimals)
    const rawPricePerRawUnit = usdPrice * scale

    // Use integer ratio with 10^6 precision
    const precision = 1_000_000
    const numerator = Math.round(rawPricePerRawUnit * precision)
    const denominator = precision

    if (numerator === 0) {
      return { price: undefined, isLoading }
    }

    const price = new Price(currency, quoteCurrency, denominator, numerator)
    return { price, isLoading }
  }, [currency, quoteCurrency, usdPrice, isLoading])
}
